<script lang="ts">
	import { ControlsContainer, Scroll, toast } from '@Shelly/shelly-design-system/index';
	import type { Device } from 'src/types/Device';
	import { format } from 'svelte-i18n';
	import { setPresenceMainConfig, setFineTuningConfig, setLedConfig } from '../../helpers/requests';
	import PresenceConfigInputs from '../subcomponents/PresenceConfigInputs.svelte';
	import FineTuningInputs from '../subcomponents/FineTuningInputs.svelte';
	import LedNightModeInputs from '../subcomponents/LedNightModeInputs.svelte';

	export let device: Device;
	export let settings: Record<string, any>;
	export let activeTab: string = 'config';

	type Page = 'configuration' | 'fine-tuning' | 'led';
	let currentPage: Page | null = null;

	$: if (activeTab !== 'config') currentPage = null;

	$: pages = [
		{
			id: 'configuration' as Page,
			label: $format('presence_page_configuration', { default: 'Configuration' }),
			icon: 'fas fa-sliders',
			description: $format('presence_page_configuration_desc', {
				default: 'Detection, height, sensitivity and mounting',
			}),
		},
		{
			id: 'fine-tuning' as Page,
			label: $format('presence_page_fine_tuning', { default: 'Fine-tuning' }),
			icon: 'fas fa-gauge-high',
			description: $format('presence_page_fine_tuning_desc', {
				default: 'Advanced thresholds and tracking parameters',
			}),
		},
		{
			id: 'led' as Page,
			label: $format('presence_page_led_night_mode', { default: 'LED & Night Mode' }),
			icon: 'fas fa-lightbulb',
			description: $format('presence_page_led_night_mode_desc', {
				default: 'LED brightness and night mode schedule',
			}),
		},
	];

	async function handleConfigSave(event: CustomEvent) {
		const config = event.detail;
		const request = await setPresenceMainConfig(device, config);
		if (request?.isok) {
			toast.open({
				type: 'success',
				title: $format('presence_toast_config_saved', { default: 'Configuration saved.' }),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('presence_toast_config_save_error', {
					default: 'Failed to save configuration.',
				}),
			});
		}
	}

	async function handleFineTuningSave(event: CustomEvent) {
		const config = event.detail;
		const request = await setFineTuningConfig(device, config);
		if (request?.isok) {
			toast.open({
				type: 'success',
				title: $format('presence_toast_fine_tuning_saved', {
					default: 'Fine-tuning settings saved.',
				}),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('presence_toast_fine_tuning_save_error', {
					default: 'Failed to save fine-tuning settings.',
				}),
			});
		}
	}

	async function handleLedSave(event: CustomEvent) {
		const config = event.detail;
		const request = await setLedConfig(device, config);
		if (request?.isok) {
			toast.open({
				type: 'success',
				title: $format('presence_toast_led_saved', { default: 'LED settings saved.' }),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('presence_toast_led_save_error', {
					default: 'Failed to save LED settings.',
				}),
			});
		}
	}
</script>

<div class="sensor-config">
	{#if currentPage === null}
		<!-- List view -->
		<ul class="config-list">
			{#each pages as page}
				<li>
					<button class="config-list__item" on:click={() => (currentPage = page.id)}>
						<span class="config-list__icon">
							<i class={page.icon} aria-hidden="true"></i>
						</span>
						<span class="config-list__text">
							<span class="config-list__label">{page.label}</span>
							<span class="config-list__desc">{page.description}</span>
						</span>
						<i class="fas fa-chevron-right config-list__chevron" aria-hidden="true"></i>
					</button>
				</li>
			{/each}
		</ul>
	{:else}
		<!-- Sub-page view: back stays fixed; fields scroll (design system Scroll). -->
		<div class="subpage">
			<button type="button" class="subpage__back" on:click={() => (currentPage = null)}>
				<i class="fas fa-chevron-left" aria-hidden="true"></i>
				{$format('button_go_back', { default: 'Go back' })}
			</button>

			<ControlsContainer
				smallerScroll
				fillSpace
				size="m"
				maxHeight={900}
				class="subpage__scroll-panel"
			>
				<svelte:fragment slot="first">
					<Scroll smallerScroll class="subpage__ds-scroll">
						<div class="subpage__scroll-body">
							{#if currentPage === 'configuration'}
								<PresenceConfigInputs
									{device}
									{settings}
									on:configSave={handleConfigSave}
								/>
							{:else if currentPage === 'fine-tuning'}
								<FineTuningInputs {settings} on:configSave={handleFineTuningSave} />
							{:else if currentPage === 'led'}
								<LedNightModeInputs {settings} on:configSave={handleLedSave} />
							{/if}
						</div>
					</Scroll>
				</svelte:fragment>
			</ControlsContainer>
		</div>
	{/if}
</div>

<style>
	.sensor-config {
		padding: 0;
		height: 100%;
		min-height: 0;
		display: flex;
		flex-direction: column;
	}

	/* ── List view ─────────────────────────────────────────────────── */
	.config-list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.config-list__item {
		width: 100%;
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px 8px;
		background: transparent;
		border: none;
		border-radius: 8px;
		cursor: pointer;
		text-align: left;
		color: inherit;
		transition: background 0.15s;
	}

	.config-list__item:hover {
		background: var(--color-surface-secondary, rgba(255, 255, 255, 0.06));
	}

	.config-list__icon {
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
		background: var(--color-surface-secondary, rgba(255, 255, 255, 0.08));
		flex-shrink: 0;
		font-size: 14px;
		color: var(--color-primary, #0088ff);
	}

	.config-list__text {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.config-list__label {
		font-size: 14px;
		font-weight: 500;
		color: var(--color-text-primary);
	}

	.config-list__desc {
		font-size: 12px;
		color: var(--color-text-secondary, rgba(255, 255, 255, 0.5));
	}

	.config-list__chevron {
		font-size: 12px;
		color: var(--color-text-secondary, rgba(255, 255, 255, 0.4));
		flex-shrink: 0;
	}

	/* ── Sub-page view ─────────────────────────────────────────────── */
	.subpage {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.subpage__back {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: transparent;
		border: none;
		cursor: pointer;
		font-size: 13px;
		color: var(--color-primary, #0088ff);
		padding: 4px 0;
		margin-bottom: 8px;
		flex-shrink: 0;
	}

	.subpage__back:hover {
		opacity: 0.8;
	}

	/* Let the design-system panel consume remaining sidebar height below “Go back”. */
	.subpage :global(.subpage__scroll-panel) {
		flex: 1;
		min-height: 0;
		min-width: 0;
		max-width: 100%;
		overflow: hidden;
	}

	.subpage :global(.subpage__scroll-panel .container) {
		min-width: 0;
		max-width: 100%;
		box-sizing: border-box;
	}

	/* Design-system Scroll uses a negative margin when the scrollbar is visible; neutralize horizontal bleed. */
	.subpage :global(.subpage__ds-scroll.controls-container-scroll) {
		overflow-x: hidden;
		overflow-y: auto;
		max-width: 100%;
		box-sizing: border-box;
	}

	.subpage :global(.subpage__ds-scroll.controls-container-scroll.scroll-visible) {
		margin-right: 0;
		padding-right: 8px;
	}

	.subpage__scroll-body {
		min-width: 0;
		max-width: 100%;
		box-sizing: border-box;
	}

	/* Full-width controls: constrain inline-flex inputs/selects to the column width. */
	.subpage__scroll-body :global(.input-wrapper) {
		width: 100%;
		max-width: 100%;
		min-width: 0;
		box-sizing: border-box;
	}

	.subpage__scroll-body :global(.row) {
		max-width: 100%;
		margin-left: 0;
		margin-right: 0;
		--bs-gutter-x: 0;
	}

	.subpage__scroll-body :global(.col),
	.subpage__scroll-body :global([class*='col-']) {
		min-width: 0;
		max-width: 100%;
		padding-left: 0;
		padding-right: 0;
	}

	.subpage__scroll-body :global(.select-wrapper) {
		width: 100%;
		max-width: 100%;
		min-width: 0;
		box-sizing: border-box;
	}

	.subpage__scroll-body :global(.info-banner) {
		max-width: 100%;
		box-sizing: border-box;
		overflow-wrap: anywhere;
	}

	.subpage__scroll-body :global(.headline-with-info) {
		flex-wrap: wrap;
		row-gap: 2px;
	}

	.subpage__scroll-body :global(.headline-with-info p) {
		min-width: 0;
	}

	.subpage__scroll-body :global(.config-actions) {
		flex-wrap: wrap;
		justify-content: flex-end;
	}
</style>
